/**
 * @file src/services/crypto.js
 * @description Client-side AES-256-GCM encryption/decryption for all sensitive data
 * 
 * SECURITY CRITICAL FILE - Handles cryptography for entire system
 * 
 * Architecture:
 * 1. User logs in → generates random 256-bit AES key (one per user, one per device)
 * 2. Key stored in localStorage (for fast access) and Firestore (for cross-device sync)
 * 3. All capsule entries encrypted with AES-256-GCM before upload
 * 4. Each encryption uses random 12-byte IV (nonce) for security
 * 5. Ciphertext = base64(IV + encrypted_data + auth_tag)
 * 6. On download, client decrypts locally (server never sees plaintext)
 * 
 * Key Rotation: Currently not implemented. Future enhancement: regenerate key when
 * user requests account security review or password change.
 * 
 * @algorithm AES-256-GCM
 *   - Block cipher: AES (Advanced Encryption Standard)
 *   - Key size: 256 bits (maximum security)
 *   - Mode: GCM (Galois/Counter Mode) - provides encryption + authentication
 *   - IV: 12 bytes (96 bits) - MUST be unique for each encryption with same key
 *   - Auth tag: 16 bytes (128 bits) - protects against tampering
 * 
 * @functions
 *   - generateAndStoreKey(uid) - Create new key on first login
 *   - importKey(uid) - Load existing key from localStorage
 *   - encryptText(plaintext, key) - Encrypt string
 *   - decryptText(ciphertext, key) - Decrypt string
 *   - encryptFile(fileBlob, key) - Encrypt binary file
 *   - decryptFile(encryptedBlob, key, mimeType) - Decrypt binary file
 * 
 * @security
 *   ✅ AES-256 (not exportable to other apps)
 *   ✅ Random IV for each operation
 *   ✅ Authenticated encryption (detect tampering)
 *   ✅ No plaintext in logs
 *   ❌ NOT: Key rotation (future)
 *   ❌ NOT: Hardware security module (future)
 * 
 * @example
 *   // Setup on login
 *   const key = await generateAndStoreKey(user.uid);
 *   
 *   // Encrypt entry
 *   const plaintext = "Medical will";
 *   const encrypted = await encryptText(plaintext, key);
 *   
 *   // Decrypt entry  
 *   const decrypted = await decryptText(encrypted, key);
 *   console.log(decrypted); // "Medical will"
 */

/**
 * VaultLight client-side encryption — AES-256-GCM via Web Crypto API
 *
 * Security model:
 *  - A random 256-bit AES-GCM key is generated the first time a user logs in.
 *  - The raw key bytes are stored in localStorage (fast path) AND synced to
 *    Firestore (encrypted at rest by Firebase, protected by security rules).
 *  - The key in Firestore allows seamless cross-device access: a new device
 *    fetches the key from Firestore on first login and caches it locally.
 *  - Firestore and Firebase Storage receive only ciphertext for entry content.
 *  - Every encrypt call uses a fresh random 96-bit IV (nonce).
 *  - Ciphertext is stored as "base64IV:base64Ciphertext" strings.
 */

const ALGO = { name: 'AES-GCM', length: 256 };
const IV_LENGTH = 12; // 96 bits — recommended for AES-GCM
const STORAGE_PREFIX = 'legaxyvault_key_';

// ── Key lifecycle ──────────────────────────────────────────────────────────────

/**
 * Generate a new random AES-256-GCM key and persist it to localStorage.
 * Only called once per user, on first login.
 */
async function generateAndStoreKey(uid) {
    const key = await window.crypto.subtle.generateKey(ALGO, true, ['encrypt', 'decrypt']);
    const raw = await window.crypto.subtle.exportKey('raw', key);
    const b64 = bufferToBase64(raw);
    localStorage.setItem(STORAGE_PREFIX + uid, b64);
    return key;
}

/**
 * Load the key from localStorage. Returns null if no key is stored for this uid.
 */
async function loadKey(uid) {
    const b64 = localStorage.getItem(STORAGE_PREFIX + uid);
    if (!b64) return null;
    const raw = base64ToBuffer(b64);
    return window.crypto.subtle.importKey('raw', raw, ALGO, true, ['encrypt', 'decrypt']);
}

/**
 * Get the encryption key for a user. Generates and stores one if it doesn't exist yet.
 * Call this once on login and cache the returned CryptoKey for the session.
 */
export async function getOrCreateKey(uid) {
    const existing = await loadKey(uid);
    if (existing) return existing;
    return generateAndStoreKey(uid);
}

/**
 * Export the user's key as a base64 string for backup / cross-device use.
 */
export async function exportKey(uid) {
    const b64 = localStorage.getItem(STORAGE_PREFIX + uid);
    if (!b64) throw new Error('No encryption key found for this account.');
    return b64;
}

/**
 * Import a previously exported base64 key string and store it for this uid.
 * Overwrites any existing key — use with care.
 */
export async function importKeyFromBase64(uid, b64) {
    // Validate the key before storing
    const raw = base64ToBuffer(b64);
    await window.crypto.subtle.importKey('raw', raw, ALGO, true, ['encrypt', 'decrypt']);
    localStorage.setItem(STORAGE_PREFIX + uid, b64);
}

/**
 * Returns true if a key exists in localStorage for the given uid.
 */
export function hasKey(uid) {
    return !!localStorage.getItem(STORAGE_PREFIX + uid);
}

// ── Encryption / Decryption ────────────────────────────────────────────────────

/**
 * Encrypt a UTF-8 string.
 * Returns a string in the format: "base64IV:base64Ciphertext"
 * 
 * Process:
 * 1. Generate random 12-byte IV (nonce) - MUST be unique per encryption
 * 2. Encode plaintext to UTF-8 bytes
 * 3. Encrypt using AES-256-GCM (includes authentication)
 * 4. Return as base64(IV) + ":" + base64(ciphertext + auth_tag)
 * 
 * WARNING: Do not reuse an IV with the same key. This function generates
 * a new IV each time, so you MUST use different keys or never encrypt
 * the same IV twice. Since we generate a new IV here, we're safe.
 * 
 * @param {string} plainText - Plaintext to encrypt
 * @param {CryptoKey} cryptoKey - AES-256 key from importKey() or generateKey()
 * @returns {string} "base64(IV):base64(ciphertext+tag)"
 * @throws {Error} If encryption fails or key is invalid
 */
export async function encryptText(plainText, cryptoKey) {
    // ─── Generate random IV (nonce) ────────────────────────────────
    // 12 bytes (96 bits) is recommended for AES-GCM
    // MUST be unique for each encryption with the same key
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));

    // ─── Encode plaintext to UTF-8 ────────────────────────────────
    // TextEncoder converts JavaScript string to UTF-8 uint8array
    const encoded = new TextEncoder().encode(plainText);

    // ─── Perform AES-256-GCM encryption ───────────────────────────
    // GCM mode provides:
    // - Encryption: AES cipher
    // - Authentication: 128-bit auth tag (detects tampering)
    // Returns: ciphertext + auth tag (concatenated by Web Crypto API)
    const cipherBuf = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },  // Algorithm + IV
        cryptoKey,                 // 256-bit key
        encoded                    // Plaintext bytes
    );

    // ─── Encode to base64 for storage ─────────────────────────────
    // Both IV and ciphertext are base64, separated by ":"
    // Format: BASE64(IV):BASE64(CIPHERTEXT+AUTH_TAG)
    return `${bufferToBase64(iv)}:${bufferToBase64(cipherBuf)}`;
}

/**
 * Decrypt a string encrypted by encryptText.
 * Returns the original UTF-8 plaintext.
 * 
 * Process:
 * 1. Split "base64IV:base64Ciphertext" into parts
 * 2. Decode from base64 to binary
 * 3. Decrypt using AES-256-GCM (verifies authentication tag)
 * 4. Decode UTF-8 bytes to plaintext
 * 
 * If ciphertext is tampered with (even 1 bit), auth tag verification
 * will fail and an error is thrown. Do NOT catch and ignore this!
 * 
 * @param {string} encrypted - Format: "base64(IV):base64(ciphertext+tag)"
 * @param {CryptoKey} cryptoKey - MUST be same key used for encryption
 * @returns {string} Original plaintext
 * @throws {Error} If decryption fails (wrong key, tampered ciphertext, bad format)
 */
export async function decryptText(encrypted, cryptoKey) {
    // ─── Parse format ─────────────────────────────────────────────
    // Ciphertext is stored as "base64IV:base64CiphertextWithTag"
    const [ivB64, cipherB64] = encrypted.split(':');

    // ─── Decode from base64 to binary ──────────────────────────────
    // Now we have the raw bytes of IV and ciphertext
    const iv = base64ToBuffer(ivB64);
    const cipherBuf = base64ToBuffer(cipherB64);

    // ─── Perform AES-256-GCM decryption ───────────────────────────
    // CRITICAL: Web Crypto will throw if:
    // - Key is wrong
    // - Ciphertext is tampered with (auth tag fails)
    // - IV is corrupted
    // This is a FEATURE - detects tampering automatically
    const plainBuf = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },  // Same IV from encryption
        cryptoKey,                 // MUST be same key
        cipherBuf                  // Ciphertext + auth tag
    );

    // ─── Decode UTF-8 bytes to string ─────────────────────────────
    // TextDecoder converts UTF-8 uint8array back to JavaScript string
    return new TextDecoder().decode(plainBuf);
}

/**
 * Encrypt a File or Blob.
 * Returns an encrypted Blob (binary ciphertext with prepended 12-byte IV).
 * The returned blob should be uploaded to Firebase Storage as-is.
 */
export async function encryptFile(file, cryptoKey) {
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const fileBuffer = await file.arrayBuffer();
    const cipherBuf = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, fileBuffer);
    // Prepend IV to ciphertext so we can extract it on decryption
    const combined = new Uint8Array(IV_LENGTH + cipherBuf.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipherBuf), IV_LENGTH);
    return new Blob([combined], { type: 'application/octet-stream' });
}

/**
 * Decrypt a Blob that was encrypted by encryptFile.
 * Returns the decrypted content as a Blob with the original MIME type.
 */
export async function decryptFile(encryptedBlob, cryptoKey, mimeType = 'application/octet-stream') {
    const buf = await encryptedBlob.arrayBuffer();
    const iv = buf.slice(0, IV_LENGTH);
    const cipherBuf = buf.slice(IV_LENGTH);
    const plainBuf = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, cipherBuf);
    return new Blob([plainBuf], { type: mimeType });
}

/**
 * Fetch an encrypted file from a URL and decrypt it, then return an object URL
 * suitable for download links or <img src>.
 * Caller is responsible for calling URL.revokeObjectURL() when done.
 */
export async function fetchAndDecryptFile(url, cryptoKey, mimeType = 'application/octet-stream') {
    const response = await fetch(url);
    const encryptedBlob = await response.blob();
    const decrypted = await decryptFile(encryptedBlob, cryptoKey, mimeType);
    return URL.createObjectURL(decrypted);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function bufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

function base64ToBuffer(b64) {
    const binary = window.atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}
